import { useEffect, useState } from "react";
import Autocomplete from "@/components/Autocomplete";
import AvatarDetails from "@/components/AvatarDetails";
import { useQuery } from "@apollo/client";
import { SOME_PEOPLE_QUERY } from "@/pages/people/queries";
import { Person } from "@/types";
import { capitalize } from "lodash";

export interface PersonAutocompleteProps {
  query: string;
  onChangeText: (value: string) => void;
  onSelect: (person: Person) => void;
  label?: string;
  required?: boolean;
  reset?: () => void;
  autofocus?: boolean;
  skip?: boolean;
}

export default function PersonAutocomplete({
  query,
  onChangeText,
  onSelect,
  label = "Person",
  required,
  reset,
  autofocus,
  skip = false
}: PersonAutocompleteProps) {
  const [variables, setVariables] = useState<Record<string, unknown>>({});
  const { data, loading: loadingResults } = useQuery<{ people?: { nodes?: Person[] } }>(SOME_PEOPLE_QUERY, {
    variables,
    skip
  });

  useEffect(() => {
    setVariables({
      ...(query ? { input: { search: query } } : {}),
      first: 20
    });
  }, [query]);

  return (
    <Autocomplete
      label={label}
      required={required}
      query={query}
      data={data?.people?.nodes || []}
      onChangeText={onChangeText}
      loadingResults={loadingResults}
      reset={reset}
      onSelect={onSelect}
      autofocus={autofocus}
      renderItem={(personData: Person) => (
        <AvatarDetails
          title={`${personData.firstName} ${personData.lastName}`}
          text={
            <div className="flex flex-col">
              <div className="text-sys-brand-on-surface-variant">{personData.pgaId}</div>
              {personData.latestProgram && (
                <div className="text-sys-brand-on-surface-variant">
                  {personData.latestProgram?.programType.name} - {capitalize(personData.latestProgram?.status)}
                </div>
              )}
            </div>
          }
          picture={personData.profilePhoto}
          secondaryText={personData.primarySectionAffiliation?.section?.name || ""}
        />
      )}
    />
  );
}
